import { UseQueryResult } from "@tanstack/react-query";
import { Flex } from "antd";
import { ColumnsType } from "antd/es/table";
import { TableProps } from "antd/lib";
import { isFunction, omit } from "lodash";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useFilters } from "../../modules/filters/filters.hooks";
import {
  FilterItem,
  PersistedFilterKeys,
} from "../../modules/filters/filters.types";
import { PaginationPayload } from "../../types";
import FilterToolbar from "../FilterToolbar/FilterToolbar";
import { Table } from "../Table/Table";
import style from "./FilterableTableLayout.module.scss";

type WithPagination<T> = T & {
  pageNumber?: number;
  pageSize?: number;
};

type GetFilterItemsArgs<
  QueryResponse extends object,
  QueryPayload extends object
> = Partial<QueryResponse> & {
  isLoading?: boolean;
  filters?: WithPagination<QueryPayload>;
};

interface FilterableTableLayoutProps<
  RecordType extends object,
  QueryResponse extends object,
  QueryPayload extends object
> extends Omit<TableProps<RecordType>, "title"> {
  tableName: string;
  /**
   * Page title
   */
  title: string;
  /**
   * Key used to access data from query response if the response is not an array of RecordType itself
   */
  dataListKey?: keyof QueryResponse;
  /**
   * ID of components' upper element  - filter toolbar and title - used to calculate tables' height
   */
  toolbarId: string;

  /**
   * Function that generates table columns (has to be a function because of possible reactive states)
   * @returns Array of table columns
   */
  getTableColumns: (filters?: QueryPayload) => ColumnsType<RecordType>;

  /**
   * Tanstack React Query hook used to fetch data
   * @param args Applied filters and enabled flag
   * @returns QueryResponse
   */
  queryHook: (args: {
    filters: QueryPayload;
    enabled?: boolean | undefined;
  }) => UseQueryResult<QueryResponse, Error>;

  /**
   * Used as FilterToolbar children prop
   */
  toolbarActions?: ReactNode;
  renderToolbarActions?: (args: {
    payload?: QueryPayload;
    data?: QueryResponse;
  }) => ReactNode;
  debounceTime?: number;
  tableRowKey?: keyof RecordType | "id";
  onTableRowClick?: (record: RecordType) => void;

  filterProps?: {
    /**
     * Initial value of filters
     */
    initialFilters?: QueryPayload;
    /**
     * Key for saving filters in store if needed
     */
    persistedFilterKey?: PersistedFilterKeys;
    /**
     * Function that generates filter items displayed in filter toolbar
     * @param args Partial reponse from query (filter dropdown values) and isLoading state
     * (NOTE: when creating this function make sure to match its' arguments with queries' response)
     * @returns
     */
    getFilterItems?: (
      args: GetFilterItemsArgs<QueryResponse, QueryPayload>
    ) => Array<FilterItem<QueryPayload>>;
    filterToolbarClassName?: string | undefined;

    /**
     * If setting filters is also done outside component this change will be tracked
     */
    outsideFilters?: Partial<QueryPayload>;
    /**
     * If user wants to trigger query manually (e.g. by button click) instead of on filters change
     */
    triggerQueryManually?: boolean;
    /**
     * Apply button is disabled while any of these filter fields are empty
     */
    applyDisabledIfEmpty?: Array<keyof QueryPayload>;
  };
  /**
   * Used to enable sending pageSize & pageNumber to useQuery hook and retrieve BE filtered data instead of FE
   */
  paginationProps?: {
    enableAPIPagination?: boolean;
    initialPagination?: PaginationPayload | undefined;
    totalCountKey?: keyof QueryResponse;
  };
  /**
   * ANT Design table props for layout
   */
  tableLayout?: "auto" | "fixed" | undefined;
  /**
   * ANT Design table props for overflowing of content
   */
  scroll?: {
    x?: number | true | string;
    y?: number | string;
  };
  renderAdditionalInfoRow?: (data: QueryResponse | undefined) => ReactNode;
}

/**
 * FilterableTableLayout is a component that uses useQueryTanstack for fetching, filtering and displaying data
 * @returns Layout with page title, filter toolbar and table with data displayed
 */
const FilterableTableLayout = <
  RecordType extends object,
  QueryResponse extends object = never,
  QueryPayload extends object = never
>({
  title,
  tableRowKey = "id",
  dataListKey,
  tableName,
  toolbarId,
  filterProps: {
    initialFilters,
    persistedFilterKey,
    getFilterItems,
    filterToolbarClassName,
    outsideFilters,
    triggerQueryManually,
    applyDisabledIfEmpty,
  } = {},
  paginationProps: {
    enableAPIPagination,
    initialPagination,
    totalCountKey,
  } = {},
  debounceTime = 400,
  toolbarActions,
  renderToolbarActions,
  queryHook,
  onTableRowClick,
  getTableColumns,
  expandable,
  tableLayout,
  scroll,
  renderAdditionalInfoRow,
  ...props
}: FilterableTableLayoutProps<RecordType, QueryResponse, QueryPayload>) => {
  const [isQueryTriggered, setIsQueryTriggered] = useState(0);

  const { filters, setFilters } = useFilters<
    WithPagination<QueryPayload> | undefined
  >({
    initialFilters: {
      ...(initialFilters ? initialFilters : ({} as QueryPayload)),
      pageNumber: initialPagination?.pageNumber ?? 1,
      pageSize: initialPagination?.pageSize ?? 10,
    } as WithPagination<QueryPayload>,
    persistedFilterKey,
  });

  const [manualFilters, setManualFilters] = useState<QueryPayload>({
    ...(initialFilters ? initialFilters : ({} as QueryPayload)),
    pageNumber: initialPagination?.pageNumber ?? 1,
    pageSize: initialPagination?.pageSize ?? 10,
  } as WithPagination<QueryPayload>);

  const debouncedFilters = useDebounce(
    omit(filters, ["pageNumber", "pageSize"]),
    debounceTime
  );

  const { dataUpdatedAt, data, isLoading, isRefetching } = queryHook({
    filters: {
      ...(triggerQueryManually
        ? manualFilters ?? ({} as QueryPayload)
        : debouncedFilters?.[0] ?? ({} as QueryPayload)),
      ...(enableAPIPagination
        ? {
            pageNumber: filters?.pageNumber,
            pageSize: filters?.pageSize,
          }
        : {}),
    } as WithPagination<QueryPayload>,
  });

  const filterItems = useMemo(() => {
    if (!dataListKey) {
      return getFilterItems?.({});
    }

    //NOTE: Query response is usually consisted of list and dropdown values, list is omitted when generating form items because we only need drodpown values for filters
    return getFilterItems?.({
      isLoading,
      ...(data ? omit(data, dataListKey) : {}),
      filters,
    } as GetFilterItemsArgs<QueryResponse, QueryPayload>);
  }, [dataListKey, data, isLoading, getFilterItems, filters]);

  const onRowHandler = (record: RecordType) => ({
    onClick: () => onTableRowClick?.(record),
  });

  const displayFilterToolbar = useMemo(() => {
    return (
      Boolean(filters) &&
      Array.isArray(filterItems) &&
      Boolean(filterItems?.length) &&
      isFunction(getFilterItems)
    );
  }, [filters, filterItems, getFilterItems]);

  const dataSource = useMemo(() => {
    return (
      dataListKey ? (data as QueryResponse)?.[dataListKey] : data
    ) as RecordType[];
  }, [data, dataListKey]);

  const totalCount = useMemo(() => {
    if (totalCountKey) {
      const total = (data as QueryResponse)?.[totalCountKey];
      const totalType = typeof total;

      switch (totalType) {
        case "number":
        case "bigint":
          return total as number;
        case "string":
          return parseInt(total as string);
        default:
          return 0;
      }
    }

    return 0;
  }, [totalCountKey, data]);

  useEffect(() => {
    setIsQueryTriggered(dataUpdatedAt);
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (outsideFilters) {
      setFilters({
        ...outsideFilters,
        ...(initialPagination
          ? {
              pageNumber: initialPagination?.pageNumber ?? 1,
              pageSize: initialPagination?.pageSize ?? 10,
            }
          : {}),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outsideFilters]);

  const appliedFilters = (
    triggerQueryManually ? manualFilters : filters
  ) as QueryPayload;

  const toolbarActionsContent =
    renderToolbarActions?.({
      payload: appliedFilters,
      data,
    }) ?? toolbarActions;

  return (
    <Flex className={style.container} gap={20} vertical>
      <Flex id={toolbarId} gap={20} vertical>
        <span className={style.title}>{title}</span>
        {displayFilterToolbar ? (
          <FilterToolbar<WithPagination<QueryPayload>>
            className={filterToolbarClassName}
            filters={filters!}
            filterItems={filterItems}
            onFilterChange={(newFilters) => {
              setFilters({
                ...newFilters,
                ...(enableAPIPagination
                  ? {
                      pageNumber: initialPagination?.pageNumber ?? 1,
                      pageSize: initialPagination?.pageSize ?? 10,
                    }
                  : {}),
              } as WithPagination<QueryPayload>);
            }}
            showApplyButton={triggerQueryManually}
            applyDisabledIfEmpty={applyDisabledIfEmpty}
            setManualFilters={(filters) => {
              setManualFilters(filters as QueryPayload);
            }}
          >
            {toolbarActionsContent}
          </FilterToolbar>
        ) : (
          <></>
        )}
        {renderAdditionalInfoRow?.(data)}
      </Flex>

      <Table
        expandable={expandable}
        rowKey={tableRowKey}
        name={tableName}
        onRow={onRowHandler}
        className={props.className}
        scroll={scroll ? scroll : { x: 1000 }}
        tableLayout={tableLayout}
        rowClassName={`pointer ${props.rowClassName}`}
        columns={getTableColumns(
          triggerQueryManually ? manualFilters : filters
        )}
        scrollConfig={[`#${toolbarId}`]}
        loading={isLoading || isRefetching}
        dataUpdatedAt={isQueryTriggered}
        dataSource={dataSource}
        {...(enableAPIPagination
          ? {
              pagination: {
                total: totalCount,
                current: filters?.pageNumber,
                pageSize: filters?.pageSize,
                onChange(page: number, pageSize: number) {
                  setFilters({
                    ...filters,
                    pageSize,
                    pageNumber: page,
                  } as WithPagination<QueryPayload>);
                },
              },
            }
          : {})}
      />
    </Flex>
  );
};

export default FilterableTableLayout;
